<template>
  <div class="mt-5">
    <div class="d-flex align-items-center" style="gap: 2.34rem; margin-bottom: 1.70rem;">
      <h1 class="title">{{ $t("risk.target.title") }}</h1>
    </div>

    <div class="rd-card w-100 mb-4">
      <div class="d-flex flex-column">
        <div class="d-flex align-items-center">
          <div class="pswitch mr-3" style="font-size: 0.9rem;">{{ $t("risk.target.extensionRequestCount") }}:</div>
          <div style="width: 100px;">
            <input type="number" v-model.number="form.extensionRequestCount" class="form-control" />
          </div>
        </div>
        <div class="w-100 my-4 mb-4"></div>
        <div class="d-flex flex-row w-100">
          <div v-for="(key, index) in ['LOWEST', 'LOW', 'MEDIUM', 'HIGH', 'HIGHEST']" :key="key" class="d-flex flex-row flex-grow-1">
            <div class="flex-grow-1" style="width: 10.625rem;">
              <div class="pswitch mb-3">
                {{ $t("risk.applied-risk-value." + key.toLowerCase()) }}
              </div>

              <sweet-input v-model="form.criticality[key].description" :disableValidation="true" layout="vertical" :label="$t('risk.target.description')" class="mb-3" />

              <div class="form-group mb-3">
                <label class="small text-muted mb-1">{{ $t("risk.target.approval-duration") }}</label>
                <div class="d-flex">
                  <input type="number" v-model.number="form.criticality[key].approvalDuration.value" class="form-control form-control-sm mr-1" required />
                  <select v-model="form.criticality[key].approvalDuration.unit" class="form-control form-control-sm">
                    <option value="WEEK">{{ $t("audit-control.unit.week") }}</option>
                    <option value="MONTH">{{ $t("audit-control.unit.month") }}</option>
                  </select>
                </div>
              </div>

              <div class="form-group mb-0">
                <label class="small text-muted mb-1">{{ $t("risk.target.resolution-duration") }}</label>
                <div class="d-flex">
                  <input type="number" v-model.number="form.criticality[key].resolutionDuration.value" class="form-control form-control-sm mr-1" required />
                  <select v-model="form.criticality[key].resolutionDuration.unit" class="form-control form-control-sm">
                    <option value="WEEK">{{ $t("audit-control.unit.week") }}</option>
                    <option value="MONTH">{{ $t("audit-control.unit.month") }}</option>
                  </select>
                </div>
              </div>
            </div>
            <div v-if="index < 4" class="line"></div>
          </div>
        </div>
      </div>
      <div class="w-100 mt-4">
        <div class="d-flexx mt-4 mt-auto ml-auto w-100 float-right" style="max-width: 24rem; gap: 0.75rem;">
          <button @click="save" :disabled="isUpdating" class="button save-button w-50 flex-shrink-1">
            {{ $t("common.save") }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import SweetInput from "@c/form/sweet-input";

export default {
  components: { SweetInput },
  data() {
    return {
      form: {
        extensionRequestCount: 2,
        criticality: this.getEmptyCriticality(),
      },
    };
  },
  computed: {
    ...mapGetters({
      target: "organizationRiskTarget/selectedEntity",
      isUpdating: "organizationRiskTarget/isUpdating",
    }),
  },
  watch: {
    target: {
      deep: true,
      immediate: true,
      handler(val) {
        if (val && val._id) {
          this.initForm(val);
        }
      },
    },
  },
  async mounted() {
    await this.fetchTarget();
  },
  methods: {
    ...mapActions("organizationRiskTarget", ["fetchTarget"]),
    getEmptyCriticality() {
      const keys = ["LOWEST", "LOW", "MEDIUM", "HIGH", "HIGHEST"];
      const obj = {};
      keys.forEach((k) => {
        obj[k] = {
          description: "",
          approvalDuration: { value: 1, unit: "WEEK" },
          resolutionDuration: { value: 1, unit: "MONTH" },
        };
      });
      return obj;
    },
    initForm(val) {
      const entity = this.prepareFormForEdit(JSON.parse(JSON.stringify(val)), []);

      const normalized = this.getEmptyCriticality();
      if (entity.criticality) {
        Object.keys(entity.criticality).forEach((key) => {
          const upperKey = key.toUpperCase();
          const item = entity.criticality[key];

          // Localization handling for description
          let desc = "";
          if (item.description) {
            if (typeof item.description === "object") {
              desc = item.description[this.currentLanguage] || item.description["tr"] || item.description["en"] || "";
            } else {
              desc = item.description;
            }
          }

          normalized[upperKey] = {
            ...item,
            description: desc,
            approvalDuration: item.approvalDuration || { value: 1, unit: "WEEK" },
            resolutionDuration: item.resolutionDuration || { value: 1, unit: "MONTH" },
          };
        });
      }

      this.form = {
        ...entity,
        criticality: normalized,
      };
    },
    async save() {
      const payload = JSON.parse(JSON.stringify(this.form));

      // Wrap description back into localized object before saving
      Object.keys(payload.criticality).forEach((key) => {
        const item = payload.criticality[key];
        item.description = { [this.currentLanguage]: item.description || "" };
      });

      try {
        await this.$store.dispatch("organizationRiskTarget/save", payload);
        this.$toast.success(this.$t("common.success"));
        await this.fetchTarget();
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "./organization-risk-decision.scss";

.rd-card .line {
  height: 14.5rem;
}
.save-button {
  background-color: #ff7c48;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 0.5rem;
  font-weight: 600;
  transition: all 0.3s;

  &:hover {
    background-color: #e15f2b;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}
</style>
